import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Disc3, Play, ShoppingCart, BadgeCheck, Trash2, MoreVertical, AlertTriangle, Pause, Video, Check, Star, HelpCircle, Apple } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import VideoWallModal from '@/components/VideoWallModal';
import { useAuth } from '@/context/AuthContext';
import { getAppleMusicTrackUrl, hasAppleMusicDirectLink } from '@/lib/appleMusic';

const SOURCE_LABEL = {
  spotify: 'Spotify',
  shazam: 'Shazam',
  apple_music: 'Apple Music',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube_music: 'YouTube Music',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  beatport: 'Beatport',
  album_art: 'Album art',
  poster: 'Poster',
  ai_discovery: 'AI Find',
  other: 'Other',
};

/**
 * PreviewBtn — owns its own <audio> element so the preview actually plays
 * when toggled. Previously this only flipped the Play/Pause icon via the
 * `isPlaying` prop while no audio was wired up (the audio element only
 * existed in MusicCardDetail.jsx). That's why preview "worked" in the modal
 * but did nothing in the list view.
 *
 * The audio element lives inside PreviewBtn and reacts to the `isPlaying`
 * prop via useEffect. When the parent flips another card's preview ON, this
 * card's `isPlaying` flips OFF and we pause + reset the audio.
 */
function PreviewBtn({ card, isPlaying, onToggle }) {
  const audioRef = useRef(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        // Browsers reject .play() if user hasn't interacted recently or
        // if the network blob isn't loadable. Soft-fail by toggling off.
        p.catch(() => onToggle?.());
      }
    } else {
      el.pause();
      try { el.currentTime = 0; } catch (_) { /* iOS Safari edge case */ }
    }
  }, [isPlaying, onToggle]);

  if (!card.preview_url) return null;
  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        data-testid="music-card-preview-button"
        className="rounded-lg"
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        <span className="ml-1.5 hidden sm:inline">{isPlaying ? 'Stop' : 'Preview'}</span>
      </Button>
      {/* Hidden audio element. `onEnded` flips state back so the icon
          returns to Play when the 30-second iTunes/Spotify preview ends. */}
      <audio
        ref={audioRef}
        src={card.preview_url}
        preload="none"
        onEnded={() => onToggle?.()}
        aria-hidden="true"
      />
    </>
  );
}

export default function MusicCardCompact({
  card,
  onOpen,
  onMarkOwned,
  onUnown,
  onDelete,
  onPreviewToggle,
  isPreviewing,
  showOwnedActions = false,
  showUnidentifiedActions = false,
  onFixUnidentified,
  // Bulk-select props: when `selectable` is true, render a checkbox on
  // the left, and clicking anywhere on the row toggles selection
  // (suppressing the normal "open detail" behavior).
  selectable = false,
  isSelected = false,
  onToggleSelect,
}) {
  const isOwned = card.status === 'owned';
  const isUnidentified = card.status === 'unidentified';
  const [videoOpen, setVideoOpen] = useState(false);
  const hasVideo = !!card.music_video;
  // Only allow in-app playback when YouTube oEmbed says it's allowed.
  // When undefined (not yet probed) we conservatively treat as NOT embeddable
  // so users never click "Watch here" and hit a broken embed.
  const isEmbeddable = card.music_video_embeddable === true;
  const { user } = useAuth() || {};
  const storefront = user?.apple_music_storefront;
  // Memoize the two Apple Music URL derivations. With 500-tile search
  // result crates these ran ~1000× per parent re-render (once for the
  // URL build, once for the direct-link check). Both functions are pure
  // in `card` + `storefront`, so caching on those keys is safe.
  // Zero functional change — just avoids the string-work churn on
  // scroll / filter / preview-toggle re-renders.
  const appleMusicUrl = useMemo(
    () => getAppleMusicTrackUrl(card, storefront),
    [card, storefront],
  );
  const appleMusicIsDirect = useMemo(() => hasAppleMusicDirectLink(card), [card]);

  // In select mode, the artwork + text taps toggle selection instead of
  // opening the detail modal. Outside select mode they behave normally.
  const handleOpen = () => {
    if (selectable) {
      onToggleSelect?.(card.id);
      return;
    }
    onOpen?.(card);
  };

  return (
    <>
    <div
      data-testid="music-card"
      className={`group relative flex items-center gap-3 p-3 rounded-xl border bg-card transition-all duration-200 hover:-translate-y-1 hover:z-10 hover:shadow-[0_10px_24px_-6px_hsl(var(--primary)/0.35)] ${
        isSelected
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 hover:translate-y-0 hover:shadow-none hover:z-0'
          : 'border-border hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]/40'
      } ${isOwned && !isSelected ? 'owned-glow' : ''}`}
    >
      {/* Selection checkbox — shown only when the parent puts the list in
          select mode. Tapping the row toggles selection (via handleOpen),
          but we still need a visible affordance so the user knows the row
          is selectable. Decorative-only: tap is captured by handleOpen. */}
      {selectable && (
        <div
          aria-hidden="true"
          className={`shrink-0 w-5 h-5 rounded-md border-2 grid place-items-center transition-colors duration-150 ${
            isSelected
              ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]'
              : 'border-border bg-background'
          }`}
          data-testid={`music-card-select-${isSelected ? 'on' : 'off'}`}
        >
          {isSelected && <Check className="w-3.5 h-3.5 text-[hsl(var(--primary-foreground))]" />}
        </div>
      )}

      {/* Artwork */}
      <button
        type="button"
        onClick={handleOpen}
        data-testid="music-card-open-detail"
        className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-secondary grid place-items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
      >
        {card.album_art_url ? (
          <img
            src={card.album_art_url}
            alt={card.title || 'cover'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : isUnidentified && card.original_image_b64 ? (
          <img
            src={`data:image/jpeg;base64,${card.original_image_b64}`}
            alt="Original screenshot"
            className="w-full h-full object-cover"
          />
        ) : (
          <Disc3 className="w-6 h-6 text-muted-foreground" />
        )}
      </button>

      {/* Text */}
      <button
        type="button"
        onClick={handleOpen}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          {card.source_app && (
            <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-mono uppercase">
              {SOURCE_LABEL[card.source_app] || card.source_app}
            </Badge>
          )}
          {appleMusicIsDirect && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
              title="Apple Music link available — tap the menu to open"
              data-testid="music-card-apple-music-badge"
            >
              <Apple className="w-3 h-3 mr-1" />
              Apple
            </Badge>
          )}
          {card.bpm_key?.tempo && (
            <Badge className="text-[10px] py-0 px-1.5 font-mono bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--primary))]/15">
              {Math.round(card.bpm_key.tempo)} BPM{card.bpm_key.key_name ? ` · ${card.bpm_key.key_name}${card.bpm_key.scale === 'minor' ? 'm' : ''}` : ''}
            </Badge>
          )}
          {isUnidentified && (
            <Badge variant="destructive" className="text-[10px] py-0 px-1.5">
              <AlertTriangle className="w-3 h-3 mr-1" /> Needs fix
            </Badge>
          )}
          {isOwned && (
            <Badge className="text-[10px] py-0 px-1.5 bg-[var(--brand-lime)] text-[#0B0F14] hover:bg-[var(--brand-lime)]">
              <BadgeCheck className="w-3 h-3 mr-1" /> Owned
            </Badge>
          )}
          {card.want && (
            <Badge
              className="text-[10px] py-0 px-1.5 bg-[#FFB24A] text-[#0B0F14] hover:bg-[#FFB24A]"
              data-testid="music-card-want-badge"
              title="On your want list"
            >
              <Star className="w-3 h-3 mr-1 fill-current" /> Want
            </Badge>
          )}
          {card.needs_review && !isUnidentified && (
            <Badge
              className="text-[10px] py-0 px-1.5 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/40 hover:bg-[hsl(var(--primary))]/15"
              data-testid="music-card-needs-review-badge"
              title="Couldn't verify this track against our music database — tap to confirm or edit"
            >
              <HelpCircle className="w-3 h-3 mr-1" /> Review
            </Badge>
          )}
        </div>
        <div className="font-display font-semibold text-foreground truncate">
          {card.title || (isUnidentified ? '(unidentified)' : 'Untitled')}
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {card.artist || (isUnidentified ? card.reason || 'Tap to fix' : 'Unknown artist')}
        </div>
      </button>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <PreviewBtn card={card} isPlaying={isPreviewing} onToggle={onPreviewToggle} />
        {/* Video Wall trigger \u2014 shown on every identified track card,
            not gated by `card.music_video`. The wall does an on-demand
            YouTube search for the exact artist+title (cached 7 days) and
            falls back to a branded "No video found" state if nothing
            matches, so it's always safe to offer. */}
        {!isUnidentified && (
          <Button
            size="sm"
            variant="secondary"
            className="rounded-lg"
            data-testid="music-card-video-button"
            onClick={(e) => {
              e.stopPropagation();
              setVideoOpen(true);
            }}
            aria-label="Search music video"
          >
            <Video className="w-4 h-4" />
            <span className="ml-1.5 hidden sm:inline">Video</span>
          </Button>
        )}
        {isUnidentified ? (
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onFixUnidentified?.(card);
            }}
            data-testid="music-card-fix-button"
            className="rounded-lg"
          >
            Fix
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-lg"
                data-testid="music-card-more-menu"
                aria-label="More actions"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleOpen} data-testid="music-card-action-open">
                <Disc3 className="w-4 h-4 mr-2" /> Open
              </DropdownMenuItem>
              {card.buy_links?.beatport && (
                <DropdownMenuItem
                  onClick={() => window.open(card.buy_links.beatport, '_blank')}
                  data-testid="music-card-action-buy"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" /> Buy…
                </DropdownMenuItem>
              )}
              {appleMusicUrl && (
                <DropdownMenuItem
                  onClick={() => window.open(appleMusicUrl, '_blank')}
                  data-testid="music-card-action-apple-music"
                  title={
                    appleMusicIsDirect
                      ? 'Open this track in Apple Music'
                      : 'Search on Apple Music'
                  }
                >
                  <Apple className="w-4 h-4 mr-2" />
                  {appleMusicIsDirect ? 'Open in Apple Music' : 'Search Apple Music'}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {showOwnedActions || isOwned ? (
                <DropdownMenuItem
                  onClick={() => onUnown?.(card)}
                  data-testid="music-card-unown-button"
                >
                  Move back to collection
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => onMarkOwned?.(card)}
                  data-testid="music-card-mark-owned-button"
                >
                  <BadgeCheck className="w-4 h-4 mr-2" /> Mark as Owned
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onDelete?.(card)}
                data-testid="music-card-delete-button"
                className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
    {!isUnidentified && (
      <VideoWallModal
        open={videoOpen}
        onOpenChange={(o) => setVideoOpen(!!o)}
        mode="single"
        track={card}
      />
    )}
    </>
  );
}
